/**
 * EmailDocButton — shared one-click "Email PDF to patient" button.
 *
 * 2026-02-19 · Placed beside every WhatsApp button across patient-facing
 * documents (Advance Receipt, Sales Invoice, Service Completion Report,
 * Payment Receipt, Appointment Confirmation, Hearing Aid Trial,
 * Audiogram / Clinical Report).
 *
 * Props:
 *   email      — patient's registered email (string, may be empty)
 *   onSend     — async () => Promise<{ok, to, filename}>  (parent calls API)
 *   size       — "sm" | "md"
 *   testid     — data-testid override
 *   disabledTitle — tooltip when disabled (default: spec string)
 *
 * Behaviour:
 *   • Disabled when `email` is empty → tooltip "No email address on file".
 *   • On click: calls `onSend()`, shows an inline spinner, then raises a
 *     `sonner` toast with the result ("Receipt emailed to {email}" on ok).
 *   • Soft-fallback: no email on file + user clicks → prompts for a one-off
 *     address. The parent handler must support an optional `?to=` arg.
 */
import React, { useState } from 'react';
import { Mail } from 'lucide-react';
import { toast } from 'sonner';

export default function EmailDocButton({
  email,
  onSend,
  size = 'sm',
  testid = 'email-doc-btn',
  disabledTitle = 'No email address on file for this patient',
  label = 'Email Receipt',
  className = '',
}) {
  const hasEmail = !!(email && String(email).trim());
  const [busy, setBusy] = useState(false);

  const sizeCls = size === 'md' ? 'px-3 py-1.5 text-xs' : 'px-2 py-1 text-[11px]';
  const base = 'inline-flex items-center gap-1 font-semibold rounded shadow-sm transition-colors';
  const enabled = 'bg-blue-600 hover:bg-blue-700 text-white';
  const disabled = 'bg-slate-200 text-slate-400 cursor-not-allowed';

  const handle = async (e) => {
    e?.stopPropagation?.();
    if (busy) return;
    let to = hasEmail ? email : null;
    if (!to) {
      // eslint-disable-next-line no-alert
      to = window.prompt('No email on file for this patient. Enter an address to send to:');
      if (!to || !to.trim()) return;
    }
    setBusy(true);
    try {
      const res = await onSend(to.trim());
      if (res?.ok) {
        toast.success(`Receipt emailed to ${res.to || to}`);
      } else {
        toast.error(res?.error || 'Email failed to queue');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Email failed';
      toast.error(typeof detail === 'string' ? detail : 'Email failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handle}
      disabled={!hasEmail && !onSend ? true : busy}
      aria-disabled={!hasEmail}
      title={hasEmail ? 'Email to patient' : disabledTitle}
      data-testid={testid}
      className={`${base} ${sizeCls} ${hasEmail ? enabled : disabled} ${className}`}
    >
      <Mail size={size === 'md' ? 14 : 12} />
      {busy ? 'Sending…' : label}
    </button>
  );
}
